import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BookOpenIcon, 
  CogIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  ShoppingBagIcon,
  PlusIcon,
  DocumentTextIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

const ContentManagement = () => {
  const [isIndexing, setIsIndexing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [indexingResults, setIndexingResults] = useState(null);
  const [availablePlaybooks, setAvailablePlaybooks] = useState([]);
  const [contentStats, setContentStats] = useState({});
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('playbooks');
  const [urlToIndex, setUrlToIndex] = useState('');
  const [urlCategory, setUrlCategory] = useState('documentation');
  const [bulkUrls, setBulkUrls] = useState('');
  const [selectedPlaybook, setSelectedPlaybook] = useState('');
  const [notionUrls, setNotionUrls] = useState('');
  const [indexedDocuments, setIndexedDocuments] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [openverseUrl, setOpenverseUrl] = useState('');
  const [bulkOpenverseUrls, setBulkOpenverseUrls] = useState('');
  
  // Openverse API state (simplified)
  const [openverseStats, setOpenverseStats] = useState({});
  
  // Title editing state
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editCategoryValue, setEditCategoryValue] = useState('');
  const [editingType, setEditingType] = useState(null);
  const [editTypeValue, setEditTypeValue] = useState('');
  const [isSavingType, setIsSavingType] = useState(false);
  const [isSavingCategory, setIsSavingCategory] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');



  useEffect(() => {
    const initializeData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchAvailablePlaybooks(),
          fetchContentStats(),
          fetchIndexedDocuments(),
          fetchOpenverseStats()
        ]);
      } finally {
        setIsLoading(false);
      }
    };
    initializeData();
  }, []);



  const fetchAvailablePlaybooks = async () => {
    try {
      console.log('Fetching available playbooks...');
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list_playbooks' })
      });
      
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.success) {
        setAvailablePlaybooks(data.result);
        console.log('Playbooks found:', data.result);
      } else {
        console.error('API returned error:', data.error);
        setStatus({ 
          type: 'error', 
          message: `Failed to fetch playbooks: ${data.error}` 
        });
      }
    } catch (error) {
      console.error('Error fetching playbooks:', error);
      setStatus({ 
        type: 'error', 
        message: `Cannot connect to API server. Please run 'npm run server' in a separate terminal, or 'npm run dev:full' to start both frontend and backend.` 
      });
    }
  };

  const fetchContentStats = async () => {
    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_content_stats' })
      });
      
      const data = await response.json();
      if (data.success) {
        setContentStats(data.result);
      }
    } catch (error) {
      console.error('Error fetching content stats:', error);
    }
  };

  const fetchIndexedDocuments = async () => {
    try {
      setIsLoadingDocuments(true);
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'list_indexed_documents' })
      });
      
      const data = await response.json();
      if (data.success) {
        setIndexedDocuments(data.result);
      }
    } catch (error) {
      console.error('Error fetching indexed documents:', error);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const indexAllPlaybooks = async (forceReindex = false) => {
    setIsIndexing(true);
    const action = forceReindex ? 'Forcing re-index of all playbooks...' : 'Starting playbook indexing...';
    setStatus({ type: 'info', message: action });
    setIndexingResults(null);

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_all_playbooks',
          forceReindex: forceReindex
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setIndexingResults(data.result);
        const { success, skipped, failed, total } = data.result;
        let message = `Indexing complete: ${success} ${forceReindex ? 'reindexed' : 'new'}`;
        if (skipped > 0) message += `, ${skipped} skipped (already indexed)`;
        if (failed > 0) message += `, ${failed} failed`;
        message += ` out of ${total} total playbooks`;
        
        setStatus({
          type: success > 0 || skipped > 0 ? 'success' : 'error',
          message: message
        });
        fetchContentStats(); // Refresh stats
        fetchIndexedDocuments(); // Refresh indexed documents
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index playbooks'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexUrl = async () => {
    if (!urlToIndex.trim()) {
      setStatus({ type: 'error', message: 'Please enter a URL to index' });
      return;
    }

    setIsIndexing(true);
    setStatus({ type: 'info', message: 'Indexing URL...' });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_url', 
          url: urlToIndex,
          category: urlCategory
        })
      });

      const data = await response.json();
      
      if (data.success) {
        if (data.result?.skipped) {
          setStatus({
            type: 'info',
            message: `URL already indexed: ${data.result?.existing?.title || urlToIndex}`
          });
        } else {
          setStatus({
            type: 'success',
            message: `Successfully indexed: ${data.result?.title || data.result?.url || 'Unknown document'}`
          });
        }
        setUrlToIndex('');
        fetchContentStats();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index URL'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexDiscounts = async () => {
    setIsIndexing(true);
    setStatus({ type: 'info', message: 'Indexing marketplace discounts...' });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'index_discounts' })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: `Successfully indexed ${data.result.success}/${data.result.total} discounts!`
        });
        fetchContentStats();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index discounts'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexSinglePlaybook = async () => {
    if (!selectedPlaybook) {
      setStatus({ type: 'error', message: 'Please select a playbook to index' });
      return;
    }

    setIsIndexing(true);
    setStatus({ type: 'info', message: `Indexing ${selectedPlaybook}...` });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_single_playbook', 
          playbookId: selectedPlaybook 
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: `Successfully indexed: ${data.result?.title || data.result?.playbookId || 'Unknown playbook'}`
        });
        setSelectedPlaybook('');
        fetchContentStats();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index playbook'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexBulkUrls = async () => {
    const urls = bulkUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url && url.startsWith('http'));

    if (urls.length === 0) {
      setStatus({ type: 'error', message: 'Please enter valid URLs (one per line)' });
      return;
    }

    setIsIndexing(true);
    setStatus({ type: 'info', message: `Indexing ${urls.length} URLs...` });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_urls', 
          urls,
          category: urlCategory
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setIndexingResults(data.result);
        const { success, skipped, failed, total } = data.result;
        let message = `Indexing complete: ${success} new`;
        if (skipped > 0) message += `, ${skipped} skipped (already indexed)`;
        if (failed > 0) message += `, ${failed} failed`;
        message += ` out of ${total} total URLs`;
        
        setStatus({
          type: 'success',
          message: message
        });
        setBulkUrls('');
        fetchContentStats();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index URLs'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexNotionUrls = async () => {
    const urls = notionUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url.length > 0 && url.includes('notion.site'));

    if (urls.length === 0) {
      setStatus({ type: 'error', message: 'Please enter valid Notion URLs (must contain notion.site)' });
      return;
    }

    setIsIndexing(true);
    setStatus({ type: 'info', message: `Indexing ${urls.length} public Notion pages...` });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_urls', 
          urls: urls,
          category: 'notion_public'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: `Successfully indexed ${data.result.success}/${data.result.total} Notion pages!`
        });
        setNotionUrls('');
        fetchContentStats();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index Notion pages'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexOpenverseResource = async () => {
    if (!openverseUrl.trim()) {
      setStatus({ type: 'error', message: 'Please enter an Openverse URL to index' });
      return;
    }

    setIsIndexing(true);
    setStatus({ type: 'info', message: 'Indexing Openverse resource...' });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_openverse_resource', 
          url: openverseUrl,
          category: 'openverse_curated'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: `Successfully indexed Openverse resource: ${data.result?.title || 'Unknown resource'}`
        });
        setOpenverseUrl('');
        fetchContentStats();
        fetchIndexedDocuments();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index Openverse resource'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const indexBulkOpenverseResources = async () => {
    const urls = bulkOpenverseUrls
      .split('\n')
      .map(url => url.trim())
      .filter(url => url && url.includes('openverse.fyi'));

    if (urls.length === 0) {
      setStatus({ type: 'error', message: 'Please enter valid Openverse URLs (must contain openverse.fyi)' });
      return;
    }

    setIsIndexing(true);
    setStatus({ type: 'info', message: `Indexing ${urls.length} Openverse resources...` });

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'index_openverse_resources', 
          urls,
          category: 'openverse_curated'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setIndexingResults(data.result);
        setStatus({
          type: 'success',
          message: `Successfully indexed ${data.result.success}/${data.result.total} Openverse resources!`
        });
        setBulkOpenverseUrls('');
        fetchContentStats();
        fetchIndexedDocuments();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index Openverse resources'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  // New Openverse API functions
    const fetchOpenverseStats = async () => {
    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get_openverse_stats' })
      });
      
      const data = await response.json();
      if (data.success) {
        setOpenverseStats(data.result);
      }
    } catch (error) {
      console.error('Error fetching Openverse stats:', error);
    }
  };



  const indexAllOpenverseResources = async () => {
    setIsIndexing(true);
    setStatus({ type: 'info', message: 'Fetching and indexing all resources from Openverse API...' });
    setIndexingResults(null);

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'index_all_openverse_resources' })
      });

      const data = await response.json();
      
      if (data.success) {
        setIndexingResults(data.result);
        const { success, skipped, failed, total, summary } = data.result;
        
        let message = `Indexing complete: ${success} indexed`;
        if (skipped > 0) message += `, ${skipped} skipped`;
        if (failed > 0) message += `, ${failed} failed`;
        message += ` out of ${total} total resources`;
        
        if (summary?.successRate) {
          message += ` (${summary.successRate} success rate)`;
        }
        
        setStatus({
          type: success > 0 ? 'success' : failed > 0 ? 'error' : 'info',
          message: message
        });
        fetchContentStats();
        fetchIndexedDocuments();
        fetchOpenverseStats();
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to index Openverse resources'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please check your OPENVERSE_API_KEY and try again.'
      });
    } finally {
      setIsIndexing(false);
    }
  };

  const deleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'delete_document', 
          documentId: documentId 
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: 'Document deleted successfully'
        });
        fetchIndexedDocuments(); // Refresh the list
        fetchContentStats(); // Refresh stats
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to delete document'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    }
  };

  const startEditingTitle = (doc) => {
    setEditingTitle(doc.id);
    setEditTitleValue(doc.title || '');
  };

  const cancelEditingTitle = () => {
    setEditingTitle(null);
    setEditTitleValue('');
  };

  const saveTitle = async (documentId) => {
    if (!editTitleValue.trim()) {
      setStatus({ type: 'error', message: 'Title cannot be empty' });
      return;
    }

    try {
      setStatus({ type: 'info', message: 'Updating title...' });
      
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'update_document_title',
          documentId,
          newTitle: editTitleValue.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: 'Title updated successfully'
        });
        setEditingTitle(null);
        setEditTitleValue('');
        fetchIndexedDocuments(); // Refresh the list
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to update title'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    }
  };

  const startEditingCategory = (doc) => {
    setEditingCategory(doc.id);
    setEditCategoryValue(doc.metadata?.category || '');
  };

  const cancelEditingCategory = () => {
    setEditingCategory(null);
    setEditCategoryValue('');
  };

  const saveCategory = async (documentId) => {
    try {
      setIsSavingCategory(true);
      setStatus({ type: 'info', message: 'Updating category...' });
      
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'update_document_category',
          documentId,
          newCategory: editCategoryValue.trim()
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: 'Category updated successfully'
        });
        setEditingCategory(null);
        setEditCategoryValue('');
        fetchIndexedDocuments(); // Refresh the list
      } else {
        setStatus({
          type: 'error',
          message: data.error || 'Failed to update category'
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsSavingCategory(false);
    }
  };

  const startEditingType = (doc) => {
    setEditingType(doc.id);
    setEditTypeValue(doc.type || '');
  };

  const cancelEditingType = () => {
    setEditingType(null);
    setEditTypeValue('');
  };

  const saveType = async (documentId) => {
    try {
      console.log('Starting type update:', { documentId, newType: editTypeValue.trim() });
      setIsSavingType(true);
      setStatus({ type: 'info', message: 'Updating type...' });
      
      const response = await fetch('/api/index-playbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: 'update_document_type',
          documentId,
          newType: editTypeValue.trim()
        })
      });

      const data = await response.json();
      console.log('Type update response:', data);
      
      if (data.success) {
        setStatus({
          type: 'success',
          message: 'Type updated successfully'
        });
        setEditingType(null);
        setEditTypeValue('');
        console.log('Refreshing document list...');
        fetchIndexedDocuments(); // Refresh the list
      } else {
        console.error('Type update failed:', data);
        setStatus({
          type: 'error',
          message: data.error || 'Failed to update type'
        });
      }
    } catch (error) {
      console.error('Type update error:', error);
      setStatus({
        type: 'error',
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsSavingType(false);
    }
  };

  const getStatusIcon = () => {
    if (!status) return null;
    
    switch (status.type) {
      case 'success':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'error':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'info':
        return <InformationCircleIcon className="w-5 h-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getFilteredDocuments = () => {
    if (typeFilter === 'all') {
      return indexedDocuments;
    }
    return indexedDocuments.filter(doc => doc.type === typeFilter);
  };

  const tabs = [
    { id: 'playbooks', label: 'Playbooks', icon: BookOpenIcon },
    { id: 'external', label: 'External URLs', icon: GlobeAltIcon },
    { id: 'newsletter', label: 'Newsletters', icon: () => (
      <svg className="w-4 h-4 text-yellow-500" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
        <path d="M3 7l8.5 5.5a1 1 0 0 0 1 0L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ) },
    { id: 'openverse', label: 'Openverse', icon: () => <span className="text-purple-400">🌌</span> },
    { id: 'discounts', label: 'Marketplace', icon: ShoppingBagIcon }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-purple-500 px-6 py-8">
          <div className="flex items-center space-x-3">
            <CogIcon className="w-8 h-8 text-white" />
            <div>
              <h1 className="text-2xl font-bold text-white">Content Management</h1>
              <p className="text-blue-100">Index content to make it searchable via AI chat</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4">
          <div className="flex space-x-1 bg-gray-900 rounded-lg p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Content Stats */}
          <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
            <h3 className="font-medium text-white mb-3">Indexed Content</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              {Object.entries(contentStats).map(([source, types]) => (
                <div key={source} className="bg-gray-800/50 rounded-lg p-3">
                  <div className="font-medium text-gray-300 mb-1 capitalize">
                    {source.replace(/_/g, ' ')}
                  </div>
                  {Object.entries(types).map(([type, count]) => (
                    <div key={type} className="text-gray-400 text-xs">
                      {type}: {count}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Playbooks Tab */}
            {activeTab === 'playbooks' && (
              <div>
                <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
                  <h3 className="font-medium text-white mb-2">Available Playbooks ({availablePlaybooks.length})</h3>
                  {availablePlaybooks.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-300">
                    {availablePlaybooks.map((playbook, index) => (
                      <div key={index} className="truncate">
                        • {playbook.replace(/-/g, ' ')}
                      </div>
                    ))}
                  </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-400 mb-2">No playbooks found</p>
                      <p className="text-xs text-gray-500">
                        This usually means the API server isn't running. 
                        <br />
                        Run <code className="bg-gray-800 px-1 rounded">npm run server</code> in a separate terminal.
                      </p>
                    </div>
                  )}
                </div>

                {/* Individual Playbook Selection */}
                <div className="mb-4 p-4 bg-gray-700/20 rounded-xl">
                  <h4 className="font-medium text-white mb-3">Index Individual Playbook</h4>
                  <div className="flex gap-3">
                    <select
                      value={selectedPlaybook}
                      onChange={(e) => setSelectedPlaybook(e.target.value)}
                      className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                    >
                      <option value="">Select a playbook...</option>
                      {availablePlaybooks.map((playbook) => (
                        <option key={playbook} value={playbook}>
                          {playbook.replace(/-/g, ' ')}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={indexSinglePlaybook}
                      disabled={isIndexing || !selectedPlaybook}
                      className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Index
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Perfect for indexing new playbooks you just added</p>
                </div>

                <div className="space-y-3">
                <button
                    onClick={() => indexAllPlaybooks(false)}
                  disabled={isIndexing}
                  className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-primary to-purple-500 text-white rounded-xl hover:from-primary-light hover:to-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                >
                  {isIndexing ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                      Indexing Playbooks...
                    </>
                  ) : (
                    <>
                      <BookOpenIcon className="w-5 h-5 mr-2" />
                      Index All Playbooks
                    </>
                  )}
                </button>
                  <p className="text-xs text-gray-400 text-center">
                  Indexes all playbooks (skips existing ones)
                </p>

                  <button
                    onClick={() => {
                      if (window.confirm('This will re-index ALL playbooks, even ones that already exist. This may take longer and will delete existing indexed content for these playbooks. Continue?')) {
                        indexAllPlaybooks(true);
                      }
                    }}
                    disabled={isIndexing}
                    className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-400 hover:to-red-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                  >
                    {isIndexing ? (
                      <>
                        <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                        Force Re-indexing...
                      </>
                    ) : (
                      <>
                        <ArrowPathIcon className="w-5 h-5 mr-2" />
                        Force Re-index All
                      </>
                    )}
                  </button>
                  <p className="text-xs text-orange-300 text-center">
                    ⚠️ Re-indexes all playbooks even if they already exist
                  </p>
                </div>
              </div>
            )}

            {/* External URLs Tab */}
            {activeTab === 'external' && (
              <div>
                {/* Single URL */}
                <div className="mb-6 p-4 bg-gray-700/20 rounded-xl">
                  <h4 className="font-medium text-white mb-3">Index Single URL</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        URL to Index
                      </label>
                      <input
                        type="url"
                        value={urlToIndex}
                        onChange={(e) => setUrlToIndex(e.target.value)}
                        placeholder="https://example.com/documentation"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        value={urlCategory}
                        onChange={(e) => setUrlCategory(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                      >
                        <option value="documentation">Documentation</option>
                        <option value="blog">Blog Post</option>
                        <option value="newsletter">Newsletter</option>
                        <option value="resource">Resource</option>
                        <option value="guide">Guide</option>
                        <option value="open_org_resource">Open Org Resource</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <button
                      onClick={indexUrl}
                      disabled={isIndexing || !urlToIndex.trim()}
                      className="w-full flex items-center justify-center px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                          Indexing...
                        </>
                      ) : (
                        <>
                          <PlusIcon className="w-4 h-4 mr-2" />
                          Index URL
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Public Notion URLs */}
                <div className="mb-6 p-4 bg-blue-900/20 rounded-xl border border-blue-500/30">
                  <h4 className="font-medium text-white mb-3 flex items-center">
                    <DocumentTextIcon className="w-4 h-4 mr-2" />
                    Public Notion Pages (No Token Required!)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Public Notion URLs (one per line)
                      </label>
                      <textarea
                        value={notionUrls}
                        onChange={(e) => setNotionUrls(e.target.value)}
                        placeholder={`https://your-workspace.notion.site/Page-1-abc123
https://your-workspace.notion.site/Page-2-def456
https://your-workspace.notion.site/Page-3-ghi789`}
                        rows={6}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary font-mono text-sm"
                      />
                    </div>
                    <div className="text-xs text-blue-300 bg-blue-900/20 p-3 rounded-lg">
                      <strong>Note:</strong> These must be publicly shared Notion pages. No integration token needed!
                    </div>

                    <button
                      onClick={indexNotionUrls}
                      disabled={isIndexing || !notionUrls.trim()}
                      className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-400 hover:to-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                          Indexing Notion Pages...
                        </>
                      ) : (
                        <>
                          <DocumentTextIcon className="w-5 h-5 mr-2" />
                          Index Notion Pages
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Bulk URLs */}
                <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
                  <h4 className="font-medium text-white mb-3">Bulk Index URLs</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        URLs (one per line)
                      </label>
                      <textarea
                        value={bulkUrls}
                        onChange={(e) => setBulkUrls(e.target.value)}
                        placeholder={`https://docs.example.com/guide-1
https://blog.example.com/post-1
https://help.example.com/article-1`}
                        rows={6}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary font-mono text-sm"
                      />
                    </div>

                    <button
                      onClick={indexBulkUrls}
                      disabled={isIndexing || !bulkUrls.trim()}
                      className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-primary to-purple-500 text-white rounded-xl hover:from-primary-light hover:to-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                          Indexing URLs...
                        </>
                      ) : (
                        <>
                          <GlobeAltIcon className="w-5 h-5 mr-2" />
                          Index All URLs
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                      Perfect for your monthly content additions
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Newsletter Tab */}
            {activeTab === 'newsletter' && (
              <div>
                <div className="mb-6 p-4 bg-yellow-900/20 rounded-xl border border-yellow-500/30">
                  <h3 className="font-medium text-white mb-2 flex items-center">
                    <svg className="w-5 h-5 text-yellow-500 mr-2" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M3 7l8.5 5.5a1 1 0 0 0 1 0L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Newsletter Content
                  </h3>
                  <p className="text-sm text-yellow-200 mb-3">
                    Index newsletters, email campaigns, and newsletter archives to make them searchable via AI chat.
                  </p>
                  
                  <div className="text-xs text-yellow-300 bg-yellow-900/20 p-3 rounded-lg">
                    <strong>Perfect for:</strong> Substack newsletters, Mailchimp campaigns, Beehiiv content, ConvertKit emails, and newsletter archives
                  </div>
                </div>

                {/* Single Newsletter URL */}
                <div className="mb-6 p-4 bg-gray-700/20 rounded-xl">
                  <h4 className="font-medium text-white mb-3">Index Single Newsletter</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Newsletter URL
                      </label>
                      <input
                        type="url"
                        value={urlToIndex}
                        onChange={(e) => setUrlToIndex(e.target.value)}
                        placeholder="https://example.substack.com/p/newsletter-title"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                      />
                    </div>

                    <button
                      onClick={() => {
                        const originalCategory = urlCategory;
                        setUrlCategory('newsletter');
                        indexUrl().finally(() => setUrlCategory(originalCategory));
                      }}
                      disabled={isIndexing || !urlToIndex.trim()}
                      className="w-full flex items-center justify-center px-4 py-2 bg-gradient-to-r from-yellow-600 to-orange-500 text-white rounded-lg hover:from-yellow-500 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                          Indexing Newsletter...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M3 7l8.5 5.5a1 1 0 0 0 1 0L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Index Newsletter
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Bulk Newsletter URLs */}
                <div className="p-4 bg-gray-700/20 rounded-xl">
                  <h4 className="font-medium text-white mb-3">Bulk Index Newsletter Archive</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Newsletter URLs (one per line)
                      </label>
                      <textarea
                        value={bulkUrls}
                        onChange={(e) => setBulkUrls(e.target.value)}
                        placeholder={`https://example.substack.com/p/newsletter-1
https://example.substack.com/p/newsletter-2
https://beehiiv.com/p/newsletter-3`}
                        className="w-full h-32 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-500 text-sm"
                      />
                    </div>
                    
                    <button
                      onClick={() => {
                        const originalCategory = urlCategory;
                        setUrlCategory('newsletter');
                        indexBulkUrls().finally(() => setUrlCategory(originalCategory));
                      }}
                      disabled={isIndexing || !bulkUrls.trim()}
                      className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-yellow-600 to-orange-500 text-white rounded-lg hover:from-yellow-500 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                          Indexing Newsletters...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                            <path d="M3 7l8.5 5.5a1 1 0 0 0 1 0L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Index All Newsletters
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                      Perfect for indexing entire newsletter archives
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Openverse Tab */}
            {activeTab === 'openverse' && (
              <div>
                {/* Openverse API Overview */}
                <div className="mb-6 p-4 bg-purple-900/20 rounded-xl border border-purple-500/30">
                  <h3 className="font-medium text-white mb-2 flex items-center">
                    <span className="text-purple-400 mr-2">🌌</span>
                    Openverse API Integration
                  </h3>
                  <p className="text-sm text-purple-200 mb-3">
                    Index all resources from the Openverse API to make them searchable via AI chat. This uses the official Openverse API with your OPENVERSE_API_KEY.
                  </p>
                  
                  <div className="text-xs text-purple-300 bg-purple-900/20 p-3 rounded-lg">
                    <strong>Ready to index:</strong> Fetch all resources from the Openverse API and make them searchable
                    <br />
                    <em>Note: OPENVERSE_API_KEY must be configured on the server</em>
                  </div>
                </div>

                {/* Main Index Button */}
                <div className="mb-6 p-4 bg-gradient-to-r from-purple-900/30 to-indigo-900/30 rounded-xl border border-purple-500/30">
                  <h4 className="font-medium text-white mb-3">Index All Openverse Resources</h4>
                  <p className="text-sm text-purple-200 mb-4">
                    This will fetch ALL published resources from the Openverse API and index them into your search system. 
                    Resources include tools, templates, guides, and more from the Openverse community.
                  </p>

                    <button
                    onClick={indexAllOpenverseResources}
                    disabled={isIndexing}
                    className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:from-purple-400 hover:to-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                        Indexing All Resources...
                        </>
                      ) : (
                        <>
                          <span className="mr-2">🌌</span>
                        Index All Openverse Resources via API
                        </>
                      )}
                    </button>
                  
                  <div className="text-xs text-purple-300 mt-3 space-y-1">
                    <div>• Fetches resources using official Openverse API</div>
                    <div>• Automatically handles pagination</div>
                    <div>• Skips already indexed resources</div>
                    <div>• Creates searchable vector embeddings for all content</div>
                  </div>
                </div>

                {/* Legacy Web Scraping Section */}
                <div className="mb-6 p-4 bg-gray-700/20 rounded-xl border border-yellow-500/30">
                  <h4 className="font-medium text-white mb-3 flex items-center">
                    <span className="text-yellow-400 mr-2">⚠️</span>
                    Legacy Web Scraping (Individual URLs)
                  </h4>
                  <p className="text-sm text-gray-300 mb-4">
                    You can still index individual Openverse URLs by scraping (useful for testing or specific resources not in the API).
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Openverse Resource URL
                      </label>
                      <input
                        type="url"
                        value={openverseUrl}
                        onChange={(e) => setOpenverseUrl(e.target.value)}
                        placeholder="https://openverse.fyi/resource/example-resource"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                      />
                    </div>

                    <button
                      onClick={indexOpenverseResource}
                      disabled={isIndexing || !openverseUrl.trim()}
                      className="w-full flex items-center justify-center px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isIndexing ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                          Scraping Resource...
                        </>
                      ) : (
                        <>
                          <GlobeAltIcon className="w-4 h-4 mr-2" />
                          Scrape Individual URL
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Marketplace Tab */}
            {activeTab === 'discounts' && (
              <div>
                <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
                  <h3 className="font-medium text-white mb-2">Marketplace Discounts</h3>
                  <p className="text-sm text-gray-400">
                    Index all discount offers and marketplace content to make them searchable via AI chat.
                  </p>
                </div>

                <button
                  onClick={indexDiscounts}
                  disabled={isIndexing}
                  className="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-primary to-purple-500 text-white rounded-xl hover:from-primary-light hover:to-purple-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
                >
                  {isIndexing ? (
                    <>
                      <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
                      Indexing Discounts...
                    </>
                  ) : (
                    <>
                      <ShoppingBagIcon className="w-5 h-5 mr-2" />
                      Index All Marketplace Content
                    </>
                  )}
                </button>
              </div>
            )}



            {/* Manage Indexed Content Tab */}
            {activeTab === 'manage' && (
              <div>
                <div className="mb-6 p-4 bg-gray-700/30 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-white">Indexed Documents ({indexedDocuments.length})</h3>
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-300">Filter by Type:</label>
                        <select
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          className="px-3 py-1 bg-gray-600 border border-gray-500 rounded-lg text-white text-sm focus:outline-none focus:border-primary"
                        >
                          <option value="all">All Types</option>
                          <option value="playbook">Playbooks</option>
                          <option value="external_url">External URLs</option>
                          <option value="newsletter">Newsletters</option>
                          <option value="openverse_resource">Openverse Resources</option>
                          <option value="openorg_resource">Open Org Resources</option>
                          <option value="discount">Discounts</option>
                          <option value="system">System</option>
                        </select>
                      </div>
                      <button
                        onClick={fetchIndexedDocuments}
                        disabled={isLoadingDocuments}
                        className="flex items-center space-x-2 px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm transition-colors"
                      >
                        <ArrowPathIcon className={`w-4 h-4 ${isLoadingDocuments ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                      </button>
                    </div>
                  </div>
                  
                  {isLoadingDocuments ? (
                    <div className="text-center py-8">
                      <ArrowPathIcon className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-400">Loading indexed documents...</p>
                    </div>
                  ) : indexedDocuments.length > 0 ? (
                    <div className="space-y-3">
                      {typeFilter !== 'all' ? (
                        // Filtered view - show all documents of selected type
                        <div className="mb-6">
                          <div className="flex items-center space-x-2 mb-3 px-2">
                            <DocumentTextIcon className="w-4 h-4 text-primary" />
                            <h4 className="font-medium text-white">
                              {typeFilter === 'openorg_resource' ? 'Open Org Resources' :
                               typeFilter === 'openverse_resource' ? 'Openverse Resources' :
                               typeFilter === 'external_url' ? 'External URLs' :
                               typeFilter === 'newsletter' ? 'Newsletters' :
                               typeFilter === 'playbook' ? 'Playbooks' :
                               typeFilter === 'discount' ? 'Discounts' :
                               typeFilter === 'system' ? 'System Documents' :
                               'Documents'} ({getFilteredDocuments().length})
                            </h4>
                          </div>
                          <div className="space-y-2">
                            {getFilteredDocuments().map((doc) => (
                              <div
                                key={doc.id}
                                className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                      {editingTitle === doc.id ? (
                                        <div className="flex items-center space-x-2 flex-1">
                                          <input
                                            type="text"
                                            value={editTitleValue}
                                            onChange={(e) => setEditTitleValue(e.target.value)}
                                            className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                                            placeholder="Enter title..."
                                            autoFocus
                                          />
                                          <button
                                            onClick={() => saveTitle(doc.id)}
                                            className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                            title="Save title"
                                          >
                                            <CheckIcon className="w-4 h-4" />
                                          </button>
                                          <button
                                            onClick={cancelEditingTitle}
                                            className="p-1 text-gray-400 hover:text-gray-300 transition-colors"
                                            title="Cancel"
                                          >
                                            <XMarkIcon className="w-4 h-4" />
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          <h5 className="font-medium text-white cursor-pointer hover:text-primary transition-colors" onClick={() => startEditingTitle(doc)}>
                                            {doc.title || doc.metadata?.title || 'Untitled Document'}
                                          </h5>
                                          <button
                                            onClick={() => startEditingTitle(doc)}
                                            className="p-1 text-gray-400 hover:text-primary transition-colors"
                                            title="Edit title"
                                          >
                                            <PencilIcon className="w-3 h-3" />
                                          </button>
                                        </>
                                      )}
                                      {editingType === doc.id ? (
                                        <div className="inline-flex items-center space-x-2">
                                          <select
                                            value={editTypeValue}
                                            onChange={(e) => setEditTypeValue(e.target.value)}
                                            className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-primary"
                                          >
                                            <option value="external_url">External URL</option>
                                            <option value="newsletter">Newsletter</option>
                                            <option value="playbook">Playbook</option>
                                            <option value="openverse_resource">Openverse Resource</option>
                                            <option value="openorg_resource">Open Org Resource</option>
                                            <option value="discount">Discount</option>
                                            <option value="system">System</option>
                                          </select>
                                          <button
                                            onClick={() => saveType(doc.id)}
                                            disabled={isSavingType}
                                            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                            title="Save changes"
                                          >
                                            {isSavingType ? (
                                              <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <CheckIcon className="w-3 h-3" />
                                            )}
                                            <span>{isSavingType ? 'Saving...' : 'Save'}</span>
                                          </button>
                                          <button
                                            onClick={cancelEditingType}
                                            className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                            title="Cancel editing"
                                          >
                                            <XMarkIcon className="w-3 h-3" />
                                            <span>Cancel</span>
                                          </button>
                                        </div>
                                      ) : (
                                        <span 
                                          className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-all ${
                                            doc.type === 'playbook' ? 'bg-blue-900/30 text-blue-300' :
                                            doc.type === 'external_url' ? 'bg-green-900/30 text-green-300' :
                                            doc.type === 'discount' ? 'bg-purple-900/30 text-purple-300' :
                                            doc.type === 'newsletter' ? 'bg-yellow-900/30 text-yellow-300' :
                                            doc.type === 'openorg_resource' ? 'bg-purple-900/30 text-purple-300' :
                                            doc.type === 'openverse_resource' ? 'bg-purple-900/30 text-purple-300' :
                                            'bg-gray-900/30 text-gray-300'
                                          }`}
                                          onClick={() => startEditingType(doc)}
                                          title="Click to edit type"
                                        >
                                          {doc.type || 'unknown'}
                                        </span>
                                      )}
                                    </div>
                                    
                                    <div className="text-sm text-gray-400 space-y-1">
                                      {(doc.url || doc.metadata?.url) && (
                                        <div className="flex items-start space-x-2">
                                          <span className="text-gray-500 flex-shrink-0">URL:</span>
                                          <a
                                            href={doc.url || doc.metadata?.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-400 hover:text-blue-300 underline break-all"
                                          >
                                            {doc.url || doc.metadata?.url}
                                          </a>
                                        </div>
                                      )}
                                      
                                      <div className="flex items-center space-x-4">
                                        <span>
                                          <span className="text-gray-500">Chunks:</span> {doc.chunk_count || 0}
                                        </span>
                                        <span>
                                          <span className="text-gray-500">Indexed:</span>{' '}
                                          {new Date(doc.created_at).toLocaleDateString()}
                                        </span>
                                      </div>
                                      
                                      {doc.metadata?.category && (
                                        <div>
                                          <span className="text-gray-500">Category:</span> 
                                          {editingCategory === doc.id ? (
                                            <div className="flex items-center space-x-2 inline-flex ml-2">
                                              <select
                                                value={editCategoryValue}
                                                onChange={(e) => setEditCategoryValue(e.target.value)}
                                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                                              >
                                                <option value="">Select category...</option>
                                                <option value="newsletter">Newsletter</option>
                                                <option value="documentation">Documentation</option>
                                                <option value="blog">Blog Post</option>
                                                <option value="resource">Resource</option>
                                                <option value="guide">Guide</option>
                                                <option value="open_org_resource">Open Org Resource</option>
                                                <option value="other">Other</option>
                                              </select>
                                              <button
                                                onClick={() => saveCategory(doc.id)}
                                                disabled={isSavingCategory}
                                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                                title="Save category changes"
                                              >
                                                {isSavingCategory ? (
                                                  <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                                ) : (
                                                  <CheckIcon className="w-3 h-3" />
                                                )}
                                                <span>{isSavingCategory ? 'Saving...' : 'Save'}</span>
                                              </button>
                                              <button
                                                onClick={cancelEditingCategory}
                                                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                                title="Cancel editing"
                                              >
                                                <XMarkIcon className="w-3 h-3" />
                                                <span>Cancel</span>
                                              </button>
                                            </div>
                                          ) : (
                                            <span 
                                              className="cursor-pointer hover:text-primary transition-colors ml-2"
                                              onClick={() => startEditingCategory(doc)}
                                            >
                                              {doc.metadata?.category || 'None'}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  startEditingCategory(doc);
                                                }}
                                                className="ml-2 p-1 text-gray-400 hover:text-primary transition-colors"
                                                title="Edit category"
                                              >
                                                <PencilIcon className="w-3 h-3" />
                                              </button>
                                            </span>
                                          )}
                                        </div>
                                      )}
                                      
                                      {doc.metadata?.description && (
                                        <div className="mt-2 p-2 bg-gray-800/50 rounded text-xs text-gray-300">
                                          {doc.metadata.description.substring(0, 150)}
                                          {doc.metadata.description.length > 150 ? '...' : ''}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <button
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to delete "${doc.title || doc.metadata?.title || 'this document'}"?`)) {
                                        deleteDocument(doc.id);
                                      }
                                    }}
                                    className="ml-4 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                    title="Delete document"
                                  >
                                    <TrashIcon className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                                             ) : (
                         // Original sectioned view
                         <div className="space-y-3">
                      {/* Openverse Resources Section */}
                      {(() => {
                        const openverseDocs = indexedDocuments.filter(doc => 
                          doc.content_source === 'openverse_api' || 
                          doc.content_source === 'openverse_directory' || 
                          doc.type === 'openverse_resource' ||
                          (doc.metadata && doc.metadata.category === 'open_org_resource')
                        );
                        
                        if (openverseDocs.length === 0) return null;
                        
                        return (
                          <div className="mb-6">
                            <div className="flex items-center space-x-2 mb-3 px-2">
                              <span className="text-purple-400">🌌</span>
                              <h4 className="font-medium text-white">Openverse Resources ({openverseDocs.length})</h4>
                            </div>
                            <div className="space-y-2">
                              {openverseDocs.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="bg-gradient-to-r from-purple-900/20 to-gray-800/50 rounded-lg p-4 border border-purple-700/30 hover:border-purple-600/50 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-2">
                                        {editingTitle === doc.id ? (
                                          <div className="flex items-center space-x-2 flex-1">
                                            <input
                                              type="text"
                                              value={editTitleValue}
                                              onChange={(e) => setEditTitleValue(e.target.value)}
                                              className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-purple-500"
                                              placeholder="Enter title..."
                                              autoFocus
                                            />
                                            <button
                                              onClick={() => saveTitle(doc.id)}
                                              className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                              title="Save title"
                                            >
                                              <CheckIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={cancelEditingTitle}
                                              className="p-1 text-gray-400 hover:text-gray-300 transition-colors"
                                              title="Cancel"
                                            >
                                              <XMarkIcon className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ) : (
                                          <>
                                            <h5 className="font-medium text-white cursor-pointer hover:text-purple-300 transition-colors" onClick={() => startEditingTitle(doc)}>
                                              {doc.title || 'Untitled Document'}
                                            </h5>
                                            <button
                                              onClick={() => startEditingTitle(doc)}
                                              className="p-1 text-gray-400 hover:text-purple-300 transition-colors"
                                              title="Edit title"
                                            >
                                              <PencilIcon className="w-3 h-3" />
                                            </button>
                                          </>
                                        )}
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-900/30 text-purple-300">
                                          openverse
                                        </span>
                                      </div>
                                      
                                      <div className="text-sm text-gray-400 space-y-1">
                                        {doc.url && (
                                          <div className="flex items-start space-x-2">
                                            <span className="text-gray-500 flex-shrink-0">Openverse:</span>
                                            <a
                                              href={doc.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-purple-400 hover:text-purple-300 underline break-all"
                                            >
                                              {doc.url}
                                            </a>
                                          </div>
                                        )}
                                        
                                        {doc.metadata?.resource_url && (
                                          <div className="flex items-start space-x-2">
                                            <span className="text-gray-500 flex-shrink-0">Resource:</span>
                                            <a
                                              href={doc.metadata.resource_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-400 hover:text-blue-300 underline break-all"
                                            >
                                              {doc.metadata.resource_url}
                                            </a>
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center space-x-4">
                                          <span>
                                            <span className="text-gray-500">Chunks:</span> {doc.chunk_count || 0}
                                          </span>
                                          <span>
                                            <span className="text-gray-500">Indexed:</span>{' '}
                                            {new Date(doc.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        
                                        {doc.metadata?.category && (
                                          <div>
                                            <span className="text-gray-500">Category:</span> {doc.metadata.category}
                                          </div>
                                        )}
                                        
                                        {doc.metadata?.description && (
                                          <div className="mt-2 p-2 bg-gray-800/50 rounded text-xs text-gray-300">
                                            {doc.metadata.description.substring(0, 150)}
                                            {doc.metadata.description.length > 150 ? '...' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete "${doc.title || 'this document'}"?`)) {
                                          deleteDocument(doc.id);
                                        }
                                      }}
                                      className="ml-4 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Delete document"
                                    >
                                      <TrashIcon className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Group other documents by type */}
                      {/* Newsletter Documents Section */}
                      {(() => {
                        const newsletterDocs = indexedDocuments.filter(doc => 
                          doc.type === 'newsletter' || 
                          (doc.metadata && doc.metadata.category === 'newsletter') ||
                          (doc.url && (doc.url.includes('substack.com') || 
                                      doc.url.includes('beehiiv.com') || 
                                      doc.url.includes('newsletter')))
                        );
                        
                        if (newsletterDocs.length === 0) return null;
                        
                        return (
                          <div className="mb-6">
                            <div className="flex items-center space-x-2 mb-3 px-2">
                              <svg className="w-4 h-4 text-yellow-500" viewBox="0 0 24 24" fill="none">
                                <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                                <path d="M3 7l8.5 5.5a1 1 0 0 0 1 0L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <h4 className="font-medium text-white">Newsletters ({newsletterDocs.length})</h4>
                            </div>
                            <div className="space-y-2">
                              {newsletterDocs.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="bg-gradient-to-r from-yellow-900/20 to-gray-800/50 rounded-lg p-4 border border-yellow-700/30 hover:border-yellow-600/50 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-2">
                                        {editingTitle === doc.id ? (
                                          <div className="flex items-center space-x-2 flex-1">
                                            <input
                                              type="text"
                                              value={editTitleValue}
                                              onChange={(e) => setEditTitleValue(e.target.value)}
                                              className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
                                            />
                                            <button
                                              onClick={() => saveTitle(doc.id)}
                                              className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                              title="Save"
                                            >
                                              <CheckIcon className="w-3 h-3" />
                                            </button>
                                            <button
                                              onClick={cancelEditingTitle}
                                              className="p-1 text-red-400 hover:text-red-300 transition-colors"
                                              title="Cancel"
                                            >
                                              <XMarkIcon className="w-3 h-3" />
                                            </button>
                                          </div>
                                        ) : (
                                          <>
                                            <h5 className="font-medium text-white cursor-pointer hover:text-yellow-400 transition-colors" onClick={() => startEditingTitle(doc)}>
                                              {doc.title || doc.metadata?.title || 'Untitled Newsletter'}
                                            </h5>
                                            <button
                                              onClick={() => startEditingTitle(doc)}
                                              className="p-1 text-gray-400 hover:text-yellow-400 transition-colors"
                                              title="Edit title"
                                            >
                                              <PencilIcon className="w-3 h-3" />
                                            </button>
                                          </>
                                        )}
                                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-500/15 to-orange-500/15 text-yellow-300 border border-yellow-400/20">
                                          Newsletter
                                        </span>
                                      </div>
                                      
                                      <div className="text-sm text-gray-400 space-y-1">
                                        {(doc.url || doc.metadata?.url) && (
                                          <div className="flex items-start space-x-2">
                                            <span className="text-gray-500 flex-shrink-0">URL:</span>
                                            <a
                                              href={doc.url || doc.metadata?.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-yellow-400 hover:text-yellow-300 underline break-all"
                                            >
                                              {doc.url || doc.metadata?.url}
                                            </a>
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center space-x-4">
                                          <span>
                                            <span className="text-gray-500">Chunks:</span> {doc.chunk_count || 0}
                                          </span>
                                          <span>
                                            <span className="text-gray-500">Indexed:</span>{' '}
                                            {new Date(doc.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        
                                        <div>
                                          <span className="text-gray-500">Category:</span> 
                                          {editingCategory === doc.id ? (
                                            <div className="flex items-center space-x-2 inline-flex ml-2">
                                              <select
                                                value={editCategoryValue}
                                                onChange={(e) => setEditCategoryValue(e.target.value)}
                                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-yellow-500"
                                              >
                                                <option value="">Select category...</option>
                                                <option value="newsletter">Newsletter</option>
                                                <option value="documentation">Documentation</option>
                                                <option value="blog">Blog Post</option>
                                                <option value="resource">Resource</option>
                                                <option value="guide">Guide</option>
                                                <option value="open_org_resource">Open Org Resource</option>
                                                <option value="other">Other</option>
                                              </select>
                                              <button
                                                onClick={() => saveCategory(doc.id)}
                                                className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                                title="Save"
                                              >
                                                <CheckIcon className="w-3 h-3" />
                                              </button>
                                              <button
                                                onClick={cancelEditingCategory}
                                                className="p-1 text-red-400 hover:text-red-300 transition-colors"
                                                title="Cancel"
                                              >
                                                <XMarkIcon className="w-3 h-3" />
                                              </button>
                                            </div>
                                          ) : (
                                            <span 
                                              className="cursor-pointer hover:text-yellow-400 transition-colors ml-2"
                                              onClick={() => startEditingCategory(doc)}
                                            >
                                              {doc.metadata?.category || 'None'}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  startEditingCategory(doc);
                                                }}
                                                className="ml-2 p-1 text-gray-400 hover:text-yellow-400 transition-colors"
                                                title="Edit category"
                                              >
                                                <PencilIcon className="w-3 h-3" />
                                              </button>
                                            </span>
                                          )}
                                        </div>
                                        
                                        {doc.metadata?.description && (
                                          <div className="mt-2 p-2 bg-gray-800/50 rounded text-xs text-gray-300">
                                            {doc.metadata.description.substring(0, 150)}
                                            {doc.metadata.description.length > 150 ? '...' : ''}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <button
                                      onClick={() => deleteDocument(doc.id)}
                                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Delete document"
                                    >
                                      <TrashIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {['external_website', 'internal_playbooks', 'marketplace_discounts', 'user_upload'].map((contentSource) => {
                        const docsForSource = indexedDocuments.filter(doc => 
                          (doc.content_source || doc.metadata?.content_source || doc.metadata?.source) === contentSource &&
                          // Exclude newsletters that are already shown in the newsletter section
                          !(doc.type === 'newsletter' || 
                            (doc.metadata && doc.metadata.category === 'newsletter') ||
                            (doc.url && (doc.url.includes('substack.com') || 
                                        doc.url.includes('beehiiv.com') || 
                                        doc.url.includes('newsletter')))) &&
                          // Exclude open org resources that are already shown in the openverse section
                          !(doc.metadata && doc.metadata.category === 'open_org_resource')
                        );
                        
                        if (docsForSource.length === 0) return null;
                        
                        const sourceLabel = {
                          'external_website': 'External URLs',
                          'internal_playbooks': 'Playbooks',
                          'marketplace_discounts': 'Marketplace',
                          'user_upload': 'User Uploads'
                        }[contentSource] || contentSource;
                        
                        const sourceIcon = {
                          'external_website': <GlobeAltIcon className="w-4 h-4" />,
                          'internal_playbooks': <BookOpenIcon className="w-4 h-4" />,
                          'marketplace_discounts': <ShoppingBagIcon className="w-4 h-4" />,
                          'user_upload': <DocumentTextIcon className="w-4 h-4" />
                        }[contentSource] || <DocumentTextIcon className="w-4 h-4" />;
                        
                        return (
                          <div key={contentSource} className="mb-6">
                            <div className="flex items-center space-x-2 mb-3 px-2">
                              {sourceIcon}
                              <h4 className="font-medium text-white">{sourceLabel} ({docsForSource.length})</h4>
                            </div>
                            <div className="space-y-2">
                              {docsForSource.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center space-x-2 mb-2">
                                        {editingTitle === doc.id ? (
                                          <div className="flex items-center space-x-2 flex-1">
                                            <input
                                              type="text"
                                              value={editTitleValue}
                                              onChange={(e) => setEditTitleValue(e.target.value)}
                                              className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                                              placeholder="Enter title..."
                                              autoFocus
                                            />
                                            <button
                                              onClick={() => saveTitle(doc.id)}
                                              className="p-1 text-green-400 hover:text-green-300 transition-colors"
                                              title="Save title"
                                            >
                                              <CheckIcon className="w-4 h-4" />
                                            </button>
                                            <button
                                              onClick={cancelEditingTitle}
                                              className="p-1 text-gray-400 hover:text-gray-300 transition-colors"
                                              title="Cancel"
                                            >
                                              <XMarkIcon className="w-4 h-4" />
                                            </button>
                                          </div>
                                        ) : (
                                          <>
                                            <h5 className="font-medium text-white cursor-pointer hover:text-primary transition-colors" onClick={() => startEditingTitle(doc)}>
                                              {doc.title || doc.metadata?.title || 'Untitled Document'}
                                            </h5>
                                            <button
                                              onClick={() => startEditingTitle(doc)}
                                              className="p-1 text-gray-400 hover:text-primary transition-colors"
                                              title="Edit title"
                                            >
                                              <PencilIcon className="w-3 h-3" />
                                            </button>
                                          </>
                                        )}
                                        {editingType === doc.id ? (
                                          <div className="inline-flex items-center space-x-2">
                                                                                          <select
                                                value={editTypeValue}
                                                onChange={(e) => {
                                                  console.log('Type dropdown (detailed) changed:', e.target.value);
                                                  setEditTypeValue(e.target.value);
                                                }}
                                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-primary"
                                              >
                                                <option value="external_url">External URL</option>
                                                <option value="newsletter">Newsletter</option>
                                                <option value="playbook">Playbook</option>
                                                <option value="openverse_resource">Openverse Resource</option>
                                                <option value="openorg_resource">Open Org Resource</option>
                                                <option value="discount">Discount</option>
                                                <option value="system">System</option>
                                              </select>
                                            <button
                                              onClick={() => saveType(doc.id)}
                                              disabled={isSavingType}
                                              className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                              title="Save changes"
                                            >
                                              {isSavingType ? (
                                                <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                              ) : (
                                                <CheckIcon className="w-3 h-3" />
                                              )}
                                              <span>{isSavingType ? 'Saving...' : 'Save'}</span>
                                            </button>
                                            <button
                                              onClick={cancelEditingType}
                                              className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                              title="Cancel editing"
                                            >
                                              <XMarkIcon className="w-3 h-3" />
                                              <span>Cancel</span>
                                            </button>
                                          </div>
                                        ) : (
                                          <span 
                                            className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-all ${
                                              doc.type === 'playbook' ? 'bg-blue-900/30 text-blue-300' :
                                              doc.type === 'external_url' ? 'bg-green-900/30 text-green-300' :
                                              doc.type === 'discount' ? 'bg-purple-900/30 text-purple-300' :
                                              doc.type === 'newsletter' ? 'bg-yellow-900/30 text-yellow-300' :
                                              doc.type === 'openorg_resource' ? 'bg-purple-900/30 text-purple-300' :
                                              doc.type === 'openverse_resource' ? 'bg-purple-900/30 text-purple-300' :
                                              'bg-gray-900/30 text-gray-300'
                                            }`}
                                            onClick={() => startEditingType(doc)}
                                            title="Click to edit type"
                                          >
                                            {doc.type || 'unknown'}
                                          </span>
                                        )}
                                      </div>
                                      
                                      <div className="text-sm text-gray-400 space-y-1">
                                        {(doc.url || doc.metadata?.url) && (
                                          <div className="flex items-start space-x-2">
                                            <span className="text-gray-500 flex-shrink-0">URL:</span>
                                            <a
                                              href={doc.url || doc.metadata?.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-400 hover:text-blue-300 underline break-all"
                                            >
                                              {doc.url || doc.metadata?.url}
                                            </a>
                                          </div>
                                        )}
                                        
                                        <div className="flex items-center space-x-4">
                                          <span>
                                            <span className="text-gray-500">Chunks:</span> {doc.chunk_count || 0}
                                          </span>
                                          <span>
                                            <span className="text-gray-500">Indexed:</span>{' '}
                                            {new Date(doc.created_at).toLocaleDateString()}
                                          </span>
                                        </div>
                                        
                                        <div>
                                          <span className="text-gray-500">Category:</span> 
                                          {editingCategory === doc.id ? (
                                            <div className="flex items-center space-x-2 inline-flex ml-2">
                                              <select
                                                value={editCategoryValue}
                                                onChange={(e) => setEditCategoryValue(e.target.value)}
                                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                                              >
                                                <option value="">Select category...</option>
                                                <option value="newsletter">Newsletter</option>
                                                <option value="documentation">Documentation</option>
                                                <option value="blog">Blog Post</option>
                                                <option value="resource">Resource</option>
                                                <option value="guide">Guide</option>
                                                <option value="open_org_resource">Open Org Resource</option>
                                                <option value="other">Other</option>
                                              </select>
                                              <button
                                                onClick={() => saveCategory(doc.id)}
                                                disabled={isSavingCategory}
                                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                                title="Save category changes"
                                              >
                                                {isSavingCategory ? (
                                                  <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                                ) : (
                                                  <CheckIcon className="w-3 h-3" />
                                                )}
                                                <span>{isSavingCategory ? 'Saving...' : 'Save'}</span>
                                              </button>
                                              <button
                                                onClick={cancelEditingCategory}
                                                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                                title="Cancel editing"
                                              >
                                                <XMarkIcon className="w-3 h-3" />
                                                <span>Cancel</span>
                                              </button>
                                            </div>
                                          ) : (
                                            <span 
                                              className="cursor-pointer hover:text-primary transition-colors ml-2"
                                              onClick={() => startEditingCategory(doc)}
                                            >
                                              {doc.metadata?.category || 'None'}
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  startEditingCategory(doc);
                                                }}
                                                className="ml-2 p-1 text-gray-400 hover:text-primary transition-colors"
                                                title="Edit category"
                                              >
                                                <PencilIcon className="w-3 h-3" />
                                              </button>
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete "${doc.title || doc.metadata?.title || 'this document'}"?`)) {
                                          deleteDocument(doc.id);
                                        }
                                      }}
                                      className="ml-4 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Delete document"
                                    >
                                      <TrashIcon className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Show ungrouped documents if any */}
                      {(() => {
                        const ungroupedDocs = indexedDocuments.filter(doc => 
                          !['external_website', 'internal_playbooks', 'marketplace_discounts', 'user_upload'].includes(
                            doc.content_source || doc.metadata?.content_source || doc.metadata?.source
                          )
                        );
                        
                        if (ungroupedDocs.length === 0) return null;
                        
                        return (
                          <div className="mb-6">
                            <div className="flex items-center space-x-2 mb-3 px-2">
                              <DocumentTextIcon className="w-4 h-4" />
                              <h4 className="font-medium text-white">Other Documents ({ungroupedDocs.length})</h4>
                            </div>
                            <div className="space-y-2">
                              {ungroupedDocs.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <h5 className="font-medium text-white mb-1">
                                        {doc.title || doc.metadata?.title || 'Untitled Document'}
                                      </h5>
                                      <div className="text-sm text-gray-400 space-y-1">
                                        <div>
                                          <span className="text-gray-500">Type:</span>{' '}
                                          {editingType === doc.id ? (
                                            <div className="inline-flex items-center space-x-2">
                                              <select
                                                value={editTypeValue}
                                                onChange={(e) => {
                                                  console.log('Type dropdown changed:', e.target.value);
                                                  setEditTypeValue(e.target.value);
                                                }}
                                                className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-primary"
                                              >
                                                <option value="external_url">External URL</option>
                                                <option value="newsletter">Newsletter</option>
                                                <option value="playbook">Playbook</option>
                                                <option value="openverse_resource">Openverse Resource</option>
                                                <option value="openorg_resource">Open Org Resource</option>
                                                <option value="discount">Discount</option>
                                                <option value="system">System</option>
                                              </select>
                                              <button
                                                onClick={() => saveType(doc.id)}
                                                disabled={isSavingType}
                                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:cursor-not-allowed text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                                title="Save changes"
                                              >
                                                {isSavingType ? (
                                                  <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                                ) : (
                                                  <CheckIcon className="w-3 h-3" />
                                                )}
                                                <span>{isSavingType ? 'Saving...' : 'Save'}</span>
                                              </button>
                                              <button
                                                onClick={cancelEditingType}
                                                className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded-md transition-all duration-200 flex items-center space-x-1 shadow-sm hover:shadow-md"
                                                title="Cancel editing"
                                              >
                                                <XMarkIcon className="w-3 h-3" />
                                                <span>Cancel</span>
                                              </button>
                                            </div>
                                          ) : (
                                            <span 
                                              className="cursor-pointer hover:text-yellow-400 transition-colors" 
                                              onClick={() => startEditingType(doc)}
                                              title="Click to edit type"
                                            >
                                              {doc.type || 'unknown'}
                                            </span>
                                          )}
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Source:</span> {doc.metadata?.source || 'unknown'}
                                        </div>
                                        {(doc.url || doc.metadata?.url) && (
                                          <div>
                                            <span className="text-gray-500">URL:</span>{' '}
                                            <a
                                              href={doc.url || doc.metadata?.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-blue-400 hover:text-blue-300 underline break-all"
                                            >
                                              {doc.url || doc.metadata?.url}
                                            </a>
                                          </div>
                                        )}
                                        <div>
                                          <span className="text-gray-500">Chunks:</span> {doc.chunk_count || 0}
                                        </div>
                                        <div>
                                          <span className="text-gray-500">Indexed:</span>{' '}
                                          {new Date(doc.created_at).toLocaleDateString()}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete "${doc.title || doc.metadata?.title || 'this document'}"?`)) {
                                          deleteDocument(doc.id);
                                        }
                                      }}
                                      className="ml-4 p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                                      title="Delete document"
                                    >
                                      <TrashIcon className="w-5 h-5" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <DocumentTextIcon className="w-12 h-12 text-gray-500 mx-auto mb-3" />
                      <p className="text-gray-400 mb-2">No indexed documents found</p>
                      <p className="text-sm text-gray-500">
                        Index some content from other tabs to see it here
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>

          {/* Status Message */}
          {status && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-6 p-4 rounded-xl flex items-center space-x-3 ${
                status.type === 'success' ? 'bg-green-900/20 border border-green-500/30' :
                status.type === 'error' ? 'bg-red-900/20 border border-red-500/30' :
                'bg-blue-900/20 border border-blue-500/30'
              }`}
            >
              {getStatusIcon()}
              <span className={`text-sm ${
                status.type === 'success' ? 'text-green-300' :
                status.type === 'error' ? 'text-red-300' :
                'text-blue-300'
              }`}>
                {status.message}
              </span>
            </motion.div>
          )}

          {/* Results */}
          {indexingResults && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 bg-gray-700/30 rounded-xl p-5"
            >
              <h3 className="font-medium text-white mb-4">Indexing Results</h3>
              
              <div className="grid grid-cols-4 gap-3 mb-4 text-center">
                <div className="bg-green-900/20 rounded-lg p-3 border border-green-500/30">
                  <div className="text-xl font-bold text-green-400">{indexingResults.success}</div>
                  <div className="text-xs text-green-300">New</div>
                </div>
                <div className="bg-yellow-900/20 rounded-lg p-3 border border-yellow-500/30">
                  <div className="text-xl font-bold text-yellow-400">{indexingResults.skipped || 0}</div>
                  <div className="text-xs text-yellow-300">Skipped</div>
                </div>
                <div className="bg-red-900/20 rounded-lg p-3 border border-red-500/30">
                  <div className="text-xl font-bold text-red-400">{indexingResults.failed}</div>
                  <div className="text-xs text-red-300">Failed</div>
                </div>
                <div className="bg-blue-900/20 rounded-lg p-3 border border-blue-500/30">
                  <div className="text-xl font-bold text-blue-400">{indexingResults.total}</div>
                  <div className="text-xs text-blue-300">Total</div>
                </div>
              </div>

              {/* Success Rate and Error Summary */}
              {indexingResults.summary && (
                <div className="mb-4 p-3 bg-gray-800/50 rounded-lg">
                  <div className="text-sm text-gray-300 space-y-1">
                    <div>
                      <span className="text-gray-500">Success Rate:</span> 
                      <span className={`ml-2 font-medium ${
                        parseFloat(indexingResults.summary.successRate) > 80 ? 'text-green-400' :
                        parseFloat(indexingResults.summary.successRate) > 50 ? 'text-yellow-400' : 
                        'text-red-400'
                      }`}>
                        {indexingResults.summary.successRate}
                      </span>
                    </div>
                    {indexingResults.summary.mostCommonError && (
                      <div>
                        <span className="text-gray-500">Most Common Error:</span> 
                        <span className="ml-2 text-red-300 capitalize">
                          {indexingResults.summary.mostCommonError.replace(/_/g, ' ')}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Error Categories */}
              {indexingResults.errorCategories && Object.keys(indexingResults.errorCategories).length > 0 && (
                <div className="mb-4 p-3 bg-red-900/10 border border-red-500/30 rounded-lg">
                  <h5 className="text-sm font-medium text-red-300 mb-2">Error Categories:</h5>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(indexingResults.errorCategories).map(([category, count]) => (
                      <div key={category} className="flex justify-between bg-red-900/20 rounded p-2">
                        <span className="text-red-200 capitalize">{category.replace(/_/g, ' ')}</span>
                        <span className="text-red-400 font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {indexingResults.details && indexingResults.details.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-300">Details:</h4>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {indexingResults.details.map((detail, index) => (
                      <div
                        key={index}
                        className={`text-xs p-2 rounded-lg ${
                          detail.status === 'success' 
                            ? 'bg-green-900/10 text-green-300' 
                            : detail.status === 'skipped'
                            ? 'bg-yellow-900/10 text-yellow-300'
                            : 'bg-red-900/10 text-red-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="flex-1 font-medium">
                            {detail.resource || detail.playbook || detail.url || detail.item}
                          </span>
                          <span className="font-medium ml-2">
                            {detail.status === 'success' ? '✓' : 
                             detail.status === 'skipped' ? '⊝' : '✗'}
                        </span>
                        </div>
                        
                        {/* Additional info for different statuses */}
                        {detail.status === 'skipped' && detail.reason && (
                          <div className="mt-1 text-yellow-400/70">
                            Reason: {detail.reason.replace('_', ' ')}
                          </div>
                        )}
                        
                        {detail.status === 'failed' && (
                          <div className="mt-1 space-y-1">
                            {detail.errorCategory && (
                              <div className="text-red-400/70">
                                Category: {detail.errorCategory.replace('_', ' ')}
                              </div>
                            )}
                            {detail.error && (
                              <div className="text-red-200/70 break-words">
                                Error: {detail.error}
                              </div>
                            )}
                            {detail.resourceId && (
                              <div className="text-red-300/50">
                                ID: {detail.resourceId}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {detail.status === 'success' && detail.resourceId && (
                          <div className="mt-1 text-green-400/70">
                            ID: {detail.resourceId}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-900/10 rounded-xl border border-blue-500/30">
            <h3 className="font-medium text-blue-300 mb-2">How it works:</h3>
            <ol className="text-sm text-blue-200 space-y-1 list-decimal list-inside">
              <li>Index content from various sources (playbooks, external sites, marketplace)</li>
              <li>AI creates vector embeddings for semantic search</li>
              <li>Content becomes searchable for ALL users via the chat interface</li>
              <li>Users get answers with citations back to original sources</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentManagement; 